import {
  forwardRef,
  type CSSProperties,
  type DragEvent,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type Ref,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import './displayCard.css'

export type DisplayCardVariant =
  | 'action'
  | 'tracker'
  | 'chip'
  | 'showcase'
  | 'playingCard'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'

export type DisplayCardCanonicalVariant = 'action' | 'tracker' | 'chip' | 'showcase' | 'playingCard'
export type DisplayCardRarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic'
export type DisplayCardState =
  | 'default'
  | 'equipped'
  | 'locked'
  | 'claimable'
  | 'featured'
  | 'maxed'
  | 'frozen'
  | 'hot'
  | 'unavailable'

export type DisplayCardAccent = string
export type DisplayCardSize = 'sm' | 'md' | 'lg' | 'xl' | 'auto'
export type DisplayCardElement = 'article' | 'section' | 'aside' | 'div' | 'button' | 'a'

export type DisplayCardProps = {
  variant: DisplayCardVariant
  rarity?: DisplayCardRarity
  state?: DisplayCardState | DisplayCardState[]
  accent?: DisplayCardAccent
  size?: DisplayCardSize
  interactive?: boolean
  onClick?: () => void
  onDoubleClick?: () => void
  disabled?: boolean
  href?: string
  media?: ReactNode
  icon?: ReactNode
  eyebrow?: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  chips?: ReactNode
  body?: ReactNode
  footer?: ReactNode
  revealContent?: ReactNode
  revealAriaLabel?: string
  className?: string
  style?: CSSProperties
  ariaLabel?: string
  as?: DisplayCardElement
  draggable?: boolean
  onDragStart?: (event: DragEvent<HTMLElement>) => void
  children?: ReactNode
}

import { normalizeDisplayCardVariant } from './displayCardHelpers'

const RARITY_LABEL: Record<DisplayCardRarity, string> = {
  common: 'Common',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
  mythic: 'Mythic',
}

export const CardRarityBadge = ({ rarity }: { rarity: DisplayCardRarity }) => (
  <span className={`displayCard__rarity displayCard__rarity--${rarity}`} aria-label={`${RARITY_LABEL[rarity]} rarity`}>
    {RARITY_LABEL[rarity]}
  </span>
)

export const DisplayCard = forwardRef<HTMLElement, DisplayCardProps>(function DisplayCard({
  variant,
  rarity,
  state = 'default',
  accent,
  size = 'md',
  interactive,
  onClick,
  onDoubleClick,
  disabled = false,
  href,
  media,
  icon,
  eyebrow,
  title,
  subtitle,
  badge,
  chips,
  body,
  footer,
  revealContent,
  revealAriaLabel,
  className,
  style,
  ariaLabel,
  as,
  draggable,
  onDragStart,
  children,
}: DisplayCardProps, ref) {
  const canonicalVariant = normalizeDisplayCardVariant(variant)
  const states = (Array.isArray(state) ? state : [state]).filter((entry) => entry !== 'default')
  const isInteractive = Boolean((interactive || onClick || onDoubleClick || href) && !disabled)
  const Component: DisplayCardElement = href ? 'a' : as ?? 'article'
  const localRef = useRef<HTMLElement | null>(null)
  const revealRef = useRef<HTMLDivElement | null>(null)
  const revealId = useId()
  const [isRevealOpen, setIsRevealOpen] = useState(false)
  const [revealPosition, setRevealPosition] = useState<CSSProperties | null>(null)
  const resolvedStyle = accent
    ? ({ ...style, ['--card-accent' as string]: accent } as CSSProperties)
    : style
  const resolvedBadge = badge ?? (rarity ? <CardRarityBadge rarity={rarity} /> : null)
  const hasReveal = revealContent != null

  const updateRevealPosition = () => {
    const node = localRef.current
    if (!node || typeof window === 'undefined') return
    const rect = node.getBoundingClientRect()
    const width = Math.min(360, Math.max(240, rect.width))
    const left = Math.min(window.innerWidth - width - 12, Math.max(12, rect.left + (rect.width / 2) - (width / 2)))
    const preferredTop = rect.bottom + 8
    const top = preferredTop > window.innerHeight - 220 ? Math.max(12, rect.top - 228) : preferredTop
    setRevealPosition({ top, left, width })
  }

  const openReveal = () => {
    if (!hasReveal) return
    updateRevealPosition()
    setIsRevealOpen(true)
  }

  const closeReveal = () => {
    if (!hasReveal) return
    setIsRevealOpen(false)
  }

  useEffect(() => {
    if (!isRevealOpen || !hasReveal) return undefined
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setIsRevealOpen(false)
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (localRef.current?.contains(target) || revealRef.current?.contains(target)) return
      setIsRevealOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', updateRevealPosition)
    window.addEventListener('scroll', updateRevealPosition, true)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', updateRevealPosition)
      window.removeEventListener('scroll', updateRevealPosition, true)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [hasReveal, isRevealOpen])

  const mergedRef = (node: HTMLElement | null) => {
    localRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) (ref as { current: HTMLElement | null }).current = node
  }

  const classNames = [
    'displayCard',
    `displayCard--${canonicalVariant}`,
    `displayCard--size-${size}`,
    rarity ? `displayCard--rarity-${rarity}` : '',
    rarity ? `is-${rarity}` : '',
    isInteractive ? 'displayCard--interactive' : '',
    hasReveal ? 'displayCard--hasReveal' : '',
    disabled ? 'is-disabled' : '',
    ...states.map((entry) => `is-${entry}`),
    className ?? '',
  ].filter(Boolean).join(' ')

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (disabled) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    onClick?.()
  }

  const handleDoubleClick = (event: MouseEvent<HTMLElement>) => {
    if (disabled) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    onDoubleClick?.()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!isInteractive || href) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onClick?.()
  }

  const handleMouseEnter = () => openReveal()
  const handleMouseLeave = () => closeReveal()
  const handleFocus = () => openReveal()
  const handleBlur = () => {
    window.setTimeout(() => {
      const active = document.activeElement
      if (!active || !localRef.current?.contains(active)) closeReveal()
    }, 0)
  }
  const handleTouchStart = () => {
    if (!hasReveal) return
    if (isRevealOpen) return
    openReveal()
  }

  const commonProps = {
    className: classNames,
    style: resolvedStyle,
    'aria-label': ariaLabel,
    'aria-describedby': isRevealOpen && hasReveal ? revealId : undefined,
    'aria-disabled': disabled || undefined,
    role: !href && isInteractive ? 'button' : undefined,
    tabIndex: !href && isInteractive ? 0 : undefined,
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
    onKeyDown: handleKeyDown,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onTouchStart: handleTouchStart,
    draggable,
    onDragStart,
  }

  const revealPortal = isRevealOpen && hasReveal && revealPosition && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={revealRef}
        id={revealId}
        className="displayCardReveal"
        role="tooltip"
        aria-label={revealAriaLabel}
        style={revealPosition}
      >
        {revealContent}
      </div>,
      document.body,
    )
    : null

  const content = (
    <>
      {media != null ? <div className="displayCard__media">{media}</div> : null}
      <div className="displayCard__main">
        {(icon != null || eyebrow != null || title != null || subtitle != null || resolvedBadge != null) ? (
          <header className="displayCard__header">
            {icon != null ? <div className="displayCard__icon" aria-hidden="true">{icon}</div> : null}
            <div className="displayCard__heading">
              {eyebrow != null ? <span className="displayCard__eyebrow">{eyebrow}</span> : null}
              {title != null ? <div className="displayCard__title">{title}</div> : null}
              {subtitle != null ? <span className="displayCard__subtitle">{subtitle}</span> : null}
            </div>
            {resolvedBadge != null ? <div className="displayCard__badge">{resolvedBadge}</div> : null}
          </header>
        ) : null}
        {chips != null ? <div className="displayCard__chips">{chips}</div> : null}
        {body != null ? <div className="displayCard__body">{body}</div> : null}
        {footer != null ? (
          <footer
            className="displayCard__footer"
            onClick={(event) => event.stopPropagation()}
          >
            {footer}
          </footer>
        ) : null}
      </div>
      {children}
      {revealPortal}
    </>
  )

  if (href) return <a ref={mergedRef as Ref<HTMLAnchorElement>} {...commonProps} href={href}>{content}</a>
  if (Component === 'button') return <button ref={mergedRef as Ref<HTMLButtonElement>} {...commonProps} type="button">{content}</button>
  if (Component === 'section') return <section ref={mergedRef as Ref<HTMLElement>} {...commonProps}>{content}</section>
  if (Component === 'aside') return <aside ref={mergedRef as Ref<HTMLElement>} {...commonProps}>{content}</aside>
  if (Component === 'div') return <div ref={mergedRef as Ref<HTMLDivElement>} {...commonProps}>{content}</div>
  return <article ref={mergedRef as Ref<HTMLElement>} {...commonProps}>{content}</article>
})

export default DisplayCard
